import { useEffect, useState } from 'react';
import { useSession, type PlanDto } from '@huaca/ui';

/**
 * Everything the landing page states about the product, fetched rather than written down.
 *
 * THE PAGE MUST RENDER WITHOUT ANY OF IT. This is the login screen: a visitor who cannot reach the
 * API still has to be able to sign in, so both requests are optional by construction. Neither is
 * awaited before first paint, neither can throw into the tree, and every sentence that needs a
 * number is simply not written when the number is absent — no spinner, no placeholder, no "—".
 *
 * WHY FETCH AT ALL, rather than build these in. Because the alternative is a page that claims two
 * sources and one price forever. Every figure here traces to something that governs the product:
 * the price and the trial to `plans.ts`, the sources and the counts to the corpus, the field and
 * filter counts to the two lists the enrichment and the facet pipeline actually iterate. The page
 * cannot overstate what the product does, because it is not the page that decides.
 */

/** Mirrors `OverviewDto` in huaca/src/products/tenders/overview.ts. */
export interface OverviewSource {
  publisher: string;
  publisherType: string;
}

export interface Overview {
  /** `null` means the corpus could not be read — NOT that we cover nobody. Never render it as zero. */
  sources: OverviewSource[] | null;
  postings: { tender: number; job: number } | null;
  fields: number;
  filters: number;
}

export interface LandingFacts {
  overview: Overview | null;
  /** Ordered as the server orders them: trial first, then cheapest paid tier upward. */
  plans: PlanDto[] | null;
}

/** The cheapest tier a visitor can actually buy — the one the price band leads with. */
export function paidPlans(plans: PlanDto[] | null): PlanDto[] {
  return (plans ?? []).filter((p) => p.purchasable);
}

/** The no-card trial, if this product has one. */
export function trialPlan(plans: PlanDto[] | null): PlanDto | null {
  return (plans ?? []).find((p) => p.trialDays !== null) ?? null;
}

/**
 * Split a price label so the amount can be set large and its unit small.
 *
 * The label is the SERVER'S copy (`display.priceLabel`, e.g. "₪2 לחודש") and is never parsed for a
 * number — only split for typography. Anything that does not match falls through whole, so a label
 * in a shape this does not expect renders correctly rather than partially.
 */
export function splitPrice(label: string): { amount: string; unit: string } {
  const m = /^\s*(₪\s*[\d,.]+)\s*(.*)$/.exec(label);
  return m ? { amount: m[1]!.replace(/\s+/g, ''), unit: m[2]!.trim() } : { amount: label, unit: '' };
}

export function useLandingFacts(): LandingFacts {
  const { client } = useSession();
  const [facts, setFacts] = useState<LandingFacts>({ overview: null, plans: null });

  useEffect(() => {
    let live = true;
    /*
      Settled independently, not `Promise.all`. One endpoint failing must not cost the page the other
      one's figures — and these two fail for unrelated reasons: /plans is served from config in
      process, /overview reads a collection.
    */
    void client
      // `publicRequest`, NOT `request`. This runs on the login screen, where there is no session:
      // `request` would try to mint a token first, be refused, and reject WITHOUT ever asking for
      // the overview — which is exactly how this page shipped with no sources list and no visible
      // request to explain it.
      .publicRequest<Overview>('/api/tenders/overview')
      .then((overview) => live && setFacts((f) => ({ ...f, overview })))
      .catch(() => undefined);
    void client
      .plans('tenders')
      .then((r) => live && setFacts((f) => ({ ...f, plans: r.plans })))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [client]);

  return facts;
}
